import { useMemo, useState } from "react";
import type { GestureResponderEvent } from "react-native";
import { Pressable } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Card, FilterPill, Text, View, Button } from "@src/components";
import { DateRangePicker, type DateRange } from "@visioni/a3s-ui/components";
import { isWeb } from "@src/utils/web-helpers";
import {
  CLASS_OPTIONS,
  GRANULARITY_DEFAULT_RULES,
  GRANULARITY_OPTIONS,
  clampGranularityToWindow,
  defaultTrafficFilters,
  effectiveWindowDays,
  granularityDisabledReason,
  isDefaultFilters,
  type TrafficClass,
  type TrafficFilters as TrafficFiltersValue,
  type TrafficGranularity,
} from "../filters/traffic-filters";

interface TrafficFiltersProps {
  filters: TrafficFiltersValue;
  onChange: (next: TrafficFiltersValue) => void;
  /** Inclusive ISO bounds of the data, used to clamp the date pickers. */
  minDate: string;
  maxDate: string;
  /** Visible span in days; drives the data-aware default/reset granularity. */
  spanDays: number;
  /**
   * Movement chip options derived from the payload (`movementOptions(trend)`).
   * Empty for single-movement sites — the Movement group is then hidden.
   */
  movements: readonly { value: string; label: string }[];
}

/**
 * Interactive controls for the RoadSense trend chart: granularity, a date
 * window, a movement chip row, and a vehicle-class chip row, plus a reset.
 * RoadSense-owned and built on shared design-system atoms (`FilterPill`,
 * a3s-ui's `DateRangePicker`, `Button`) — it holds no filter state itself, but
 * reports changes up so the owning screen can decide where that state lives
 * (URL params on the live report, a local hook on the dev preview).
 *
 * Movement and Classification are **multi-select**: each row has an "All" chip
 * that clears the specific picks, and picking a specific value clears "All", so
 * the state is always either All or an explicit subset (see `traffic-filters`
 * for the chart semantics — classes sum, movements draw as distinct lines with
 * a dashed "All" sum line when more than one is shown).
 *
 * The summary line mirrors the control order exactly (Granularity · Date range
 * · Movement · Classification), each item a `Label: value` pair joined by pipes
 * so a reader can map each summary item back to its control.
 */
export function TrafficFilters({ filters, onChange, minDate, maxDate, spanDays, movements }: TrafficFiltersProps) {
  const showReset = !isDefaultFilters(filters, spanDays);
  const summary = useMemo(() => buildSummary(filters, movements), [filters, movements]);

  // Days the *selected* window spans — what the granularity caps measure. Used
  // both to disable sub-day chips and to downgrade a now-too-coarse selection
  // when the date range widens.
  const windowDays = useMemo(() => effectiveWindowDays(filters, minDate, maxDate), [filters, minDate, maxDate]);

  const setGranularity = (granularity: TrafficGranularity) => onChange({ ...filters, granularity });
  // Downgrade the granularity (to Day) if the *new* window outgrows it — used
  // whenever the date range changes so the chart never sits on a sub-day bucket
  // the window can no longer support.
  const clampToWindow = (next: TrafficFiltersValue): TrafficFiltersValue =>
    clampGranularityToWindow(next, effectiveWindowDays(next, minDate, maxDate));
  // A cleared range resets both bounds to the data edges (null = "no bound");
  // picks are clamped to the data, since presets can reach past it.
  const setRange = ({ start, end }: DateRange) =>
    onChange(
      clampToWindow({
        ...filters,
        fromDate: start ? clampDayKey(localDateKey(start), minDate, maxDate) : null,
        toDate: end ? clampDayKey(localDateKey(end), minDate, maxDate) : null,
      }),
    );
  const reset = () => onChange(defaultTrafficFilters(spanDays));

  const toggleMovement = (id: string) => onChange({ ...filters, movements: toggleValue(filters.movements, id) });
  const toggleClass = (c: TrafficClass) =>
    onChange({ ...filters, classifications: toggleValue(filters.classifications, c) });

  return (
    <View style={styles.raised}>
      <Card gap="lg">
        <View flexDirection="row" justifyContent="space-between" alignItems="center" gap="md" flexWrap="wrap">
          <View flex={1} minWidth={180}>
            <FilterGroup label="Granularity" help={<GranularityHelp />}>
              <View flexDirection="row" gap="xs" flexWrap="wrap">
                {GRANULARITY_OPTIONS.map((opt) => (
                  <GranularityPill
                    key={opt.value}
                    label={opt.label}
                    isActive={filters.granularity === opt.value}
                    disabledReason={granularityDisabledReason(opt.value, windowDays)}
                    onPress={() => setGranularity(opt.value)}
                  />
                ))}
              </View>
            </FilterGroup>
          </View>

          {showReset ?
            <Button variant="secondary" variantColor="grey" label="Reset filters" onPress={reset} />
          : null}
        </View>

        <View style={styles.raised}>
          <FilterGroup label="Date range" help={<DateRangeHelp minDate={minDate} maxDate={maxDate} />}>
            <View style={styles.dateRange}>
              <DateRangePicker
                value={{
                  start: localDateFromKey(filters.fromDate ?? minDate),
                  end: localDateFromKey(filters.toDate ?? maxDate),
                }}
                onChange={setRange}
                minDate={localDateFromKey(minDate) ?? undefined}
                maxDate={localDateFromKey(maxDate) ?? undefined}
                today={localDateFromKey(maxDate) ?? undefined}
              />
            </View>
          </FilterGroup>
        </View>

        {movements.length > 0 ?
          <FilterGroup label="Movement">
            <View flexDirection="row" gap="xs" flexWrap="wrap">
              <FilterPill
                label="All"
                isActive={filters.movements.length === 0}
                onPress={() => onChange({ ...filters, movements: [] })}
              />
              {movements.map((opt) => (
                <FilterPill
                  key={opt.value}
                  label={opt.label}
                  isActive={filters.movements.includes(opt.value)}
                  onPress={() => toggleMovement(opt.value)}
                />
              ))}
            </View>
          </FilterGroup>
        : null}

        <FilterGroup label="Classification">
          <View flexDirection="row" gap="xs" flexWrap="wrap">
            <FilterPill
              label="All"
              isActive={filters.classifications.length === 0}
              onPress={() => onChange({ ...filters, classifications: [] })}
            />
            {CLASS_OPTIONS.map((opt) => (
              <FilterPill
                key={opt.value}
                label={opt.label}
                isActive={filters.classifications.includes(opt.value)}
                onPress={() => toggleClass(opt.value)}
              />
            ))}
          </View>
        </FilterGroup>

        <Text textStyle="xs/regular" color="textMuted">
          {summary}
        </Text>
      </Card>
    </View>
  );
}

interface FilterGroupProps {
  label: string;
  /** Optional help affordance rendered inline after the label (e.g. a popover). */
  help?: React.ReactNode;
  children: React.ReactNode;
}

function FilterGroup({ label, help, children }: FilterGroupProps) {
  return (
    <View gap="xs">
      <View flexDirection="row" alignItems="center" gap="xs">
        <Text textStyle="sm/semibold" color="textLabel" letterSpacing={0.5} textTransform="uppercase">
          {label}
        </Text>
        {help}
      </View>
      {children}
    </View>
  );
}

interface GranularityPillProps {
  label: string;
  isActive: boolean;
  /** Why this granularity is unavailable, or `null` if selectable. */
  disabledReason: string | null;
  onPress: () => void;
}

/**
 * A granularity chip that, when the selected date range outgrows it, renders
 * disabled **and** explains why — a granularity can't just silently stop
 * working. An enabled chip is the plain `FilterPill`. A disabled chip stays
 * pressable (so it isn't an inert dead spot) but, instead of changing
 * granularity, toggles a popover with the reason; on web the popover also shows
 * on hover so the explanation is discoverable without a click. The press is
 * intercepted, so a disabled granularity can never be selected.
 */
function GranularityPill({ label, isActive, disabledReason, onPress }: GranularityPillProps) {
  const { theme } = useUnistyles();
  const [open, setOpen] = useState(false);

  if (disabledReason === null) {
    return <FilterPill label={label} isActive={isActive} onPress={onPress} />;
  }

  // Hover (web) reveals the reason without committing; tap toggles it so the
  // explanation is reachable on touch too. Either way `onPress` is never called.
  const hoverProps = isWeb ? ({ onMouseEnter: () => setOpen(true), onMouseLeave: () => setOpen(false) } as object) : {};
  const handlePress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <View style={styles.pillAnchor} {...hoverProps}>
      <Pressable onPress={handlePress} accessibilityState={{ disabled: true }} accessibilityHint={disabledReason}>
        <View
          px="xl"
          py="sm"
          rounded={theme.spacing.md}
          style={{ backgroundColor: "transparent", borderWidth: 1, borderColor: theme.colors.border, opacity: 0.5 }}
        >
          <Text textStyle="sm/medium" color="textMuted">
            {label}
          </Text>
        </View>
      </Pressable>
      {open ?
        <View pointerEvents="none" style={styles.popover}>
          <Text textStyle="xs/regular" color="text">
            {disabledReason}
          </Text>
        </View>
      : null}
    </View>
  );
}

/**
 * Shared "?" affordance for the filter labels — a small badge whose popover
 * reveals on hover (web) and toggles on tap (touch), so the explanation is
 * reachable on both. Extracted so each help popover behaves identically.
 */
function HelpBadge({ accessibilityLabel, children }: { accessibilityLabel: string; children: React.ReactNode }) {
  const { theme } = useUnistyles();
  const [open, setOpen] = useState(false);

  const hoverProps = isWeb ? ({ onMouseEnter: () => setOpen(true), onMouseLeave: () => setOpen(false) } as object) : {};
  const handlePress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <View style={styles.pillAnchor} {...hoverProps}>
      <Pressable onPress={handlePress} accessibilityRole="button" accessibilityLabel={accessibilityLabel}>
        <View
          style={[
            styles.helpBadge,
            { borderColor: theme.colors.border, backgroundColor: open ? theme.colors.surfaceCard : "transparent" },
          ]}
        >
          <Text textStyle="xs/semibold" color="textMuted">
            ?
          </Text>
        </View>
      </Pressable>
      {open ?
        <View pointerEvents="none" style={[styles.popover, styles.helpPopover]}>
          {children}
        </View>
      : null}
    </View>
  );
}

/**
 * Explains which granularity a report opens on. The default is keyed to the
 * report's **available** data span, not the user's selection (selection only
 * ever downgrades a too-coarse pick) — easy to misread, hence the explicit
 * "available". Rows come from {@link GRANULARITY_DEFAULT_RULES} so the help
 * can't drift from the logic that picks the default.
 */
function GranularityHelp() {
  return (
    <HelpBadge accessibilityLabel="Default granularity by available date range">
      <Text textStyle="xs/semibold" color="text">
        Default by available date range
      </Text>
      <View gap="xxs" mt="xs">
        {GRANULARITY_DEFAULT_RULES.map((rule) => (
          <View key={rule.label} flexDirection="row" justifyContent="space-between" gap="md">
            <Text textStyle="xs/medium" color="text">
              {rule.label}
            </Text>
            <Text textStyle="xs/regular" color="textMuted">
              {rule.range}
            </Text>
          </View>
        ))}
      </View>
    </HelpBadge>
  );
}

/**
 * Explains the date picker's bounds: it's clamped to the data the report
 * captured, which can extend well beyond the selected range (a report may carry
 * the asset's full hire history). Without this the picker's limits look
 * arbitrary. Bounds are the available data edges (`minDate`/`maxDate`).
 */
function DateRangeHelp({ minDate, maxDate }: { minDate: string; maxDate: string }) {
  return (
    <HelpBadge accessibilityLabel="Available date range">
      <Text textStyle="xs/semibold" color="text">
        Available date range
      </Text>
      <Text textStyle="xs/regular" color="textMuted" mt="xs">
        This report may hold more data than the selected date range. Data captured at report generation runs from{" "}
        {formatBound(minDate)} to {formatBound(maxDate)}.
      </Text>
    </HelpBadge>
  );
}

const POPOVER_WIDTH = 220;

const styles = StyleSheet.create((theme) => ({
  // Stacks the date picker's popover above what follows (later filter rows, the chart).
  raised: {
    position: "relative",
    zIndex: 1,
  },
  // Fits "DD/MM/YYYY - DD/MM/YYYY" plus the calendar icon.
  dateRange: {
    maxWidth: 320,
  },
  // Anchors the absolutely-positioned popover to its pill.
  pillAnchor: {
    position: "relative",
  },
  // Floats above the pill (the filter row is near the top of the report, so
  // upward keeps it on-screen). Width is fixed so the reason wraps predictably.
  popover: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    marginBottom: theme.spacing.xs,
    width: POPOVER_WIDTH,
    maxWidth: POPOVER_WIDTH,
    backgroundColor: theme.colors.surfaceCard,
    borderColor: theme.colors.borderCard,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    zIndex: 10,
  },
  helpBadge: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  // Wider than the disabled-reason popover, which only holds a single line.
  helpPopover: {
    width: 200,
    maxWidth: 200,
  },
}));

/**
 * Add `value` to the multi-select array, or remove it if already present.
 * Returns a new array (never mutates) so React state updates cleanly.
 */
function toggleValue<T>(current: readonly T[], value: T): T[] {
  return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
}

/**
 * One-line echo of the active filters. Items appear in the **same order as the
 * controls above** (Granularity · Date range · Movement · Classification), each
 * a `Label: value` pair joined by " · ", so the summary reads as a legend for
 * the controls. Movement is omitted when the site has no movements to choose
 * from. Multi-select rows list every selected value (or "All" when empty).
 */
function buildSummary(filters: TrafficFiltersValue, movements: readonly { value: string; label: string }[]): string {
  const granularity = GRANULARITY_OPTIONS.find((o) => o.value === filters.granularity)?.label ?? "Day";
  const range =
    filters.fromDate || filters.toDate ? `${filters.fromDate ?? "start"} to ${filters.toDate ?? "end"}` : "Full window";

  const movementValue =
    filters.movements.length === 0 ?
      "All"
    : movements
        .filter((m) => filters.movements.includes(m.value))
        .map((m) => m.label)
        .join(", ");
  const classValue =
    filters.classifications.length === 0 ?
      "All"
    : CLASS_OPTIONS.filter((c) => filters.classifications.includes(c.value))
        .map((c) => c.label)
        .join(", ");

  const items = [`Granularity: ${granularity}`, `Date range: ${range}`];
  if (movements.length > 0) items.push(`Movement: ${movementValue}`);
  items.push(`Classification: ${classValue}`);
  return items.join(" · ");
}

/**
 * Format an available-bound key (`YYYY-MM-DD`) for the date-range help text,
 * e.g. "25 Dec 2024". The key is already the site's local calendar date; UTC
 * parsing is used only to format those date components without letting the
 * viewer's browser timezone shift the label.
 */
function formatBound(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * The a3s-ui picker works in local-midnight `Date`s; the filters keep
 * `YYYY-MM-DD` keys. Converting through local components keeps the calendar
 * day intact in any viewer timezone.
 */
function localDateFromKey(dayKey: string): Date | null {
  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function localDateKey(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${String(date.getFullYear())}-${mm}-${dd}`;
}

function clampDayKey(dayKey: string, minDate: string, maxDate: string): string {
  if (dayKey < minDate) return minDate;
  if (dayKey > maxDate) return maxDate;
  return dayKey;
}
